import { z } from "zod";
import { pushPlatformValues, taskStatusValues } from "../enums";

// API-boundary contracts for the device push-token endpoints. Plain zod
// (no drizzle / pg-core) so the mobile bundle stays decoupled from the DB
// package. Both the API route handler and the mobile client import these, so
// the request shapes can't drift.

// Register-or-refresh a device's push token. Upserts on (user, device,
// platform): the same install re-registering rotates the token onto one row.
export const RegisterPushTokenSchema = z.object({
  appVersion: z.string().optional(),
  // Capacitor `Device.getId()` install id — the stable key for this device.
  deviceId: z.string().min(1),
  platform: z.enum(pushPlatformValues),
  token: z.string().min(1),
});
export type RegisterPushTokenInput = z.infer<typeof RegisterPushTokenSchema>;

// Deregister this device's tokens (DELETE /push/tokens).
export const DeletePushTokenSchema = z.object({
  deviceId: z.string().min(1),
});

// Names the device on a native sign-out, so the server drops its tokens
// (apps/api/src/utils/auth.ts).
export const SIGN_OUT_DEVICE_ID_HEADER = "x-vidimo-device-id";

// ── Live Activity per-activity push tokens ──────────────────────────────────
//
// Sent to `POST /tasks/:id/live-activity-token` when iOS issues (or rotates)
// the ActivityKit push token for a task's Live Activity, and DELETE'd when the
// activity ends. The task id comes from the path; the body carries only the
// per-device fields. Live Activity is iOS-only, so the platform is implied.

export const RegisterLiveActivityTokenSchema = z.object({
  appVersion: z.string().optional(),
  // Capacitor `Device.getId()` install id — scopes the token to a device so a
  // refresh updates one row and a logout/end can prune it.
  deviceId: z.string().min(1),
  token: z.string().min(1),
});
export type RegisterLiveActivityTokenInput = z.infer<
  typeof RegisterLiveActivityTokenSchema
>;

export const DeleteLiveActivityTokenSchema = z.object({
  deviceId: z.string().min(1),
});
export type DeleteLiveActivityTokenInput = z.infer<
  typeof DeleteLiveActivityTokenSchema
>;

// The content-state pushed to a task's Live Activity. This is the wire contract
// for the APNs `content-state` field: its keys MUST match the iOS
// `TaskActivityAttributes.ContentState` struct (the widget decodes it), so keep
// the two in lock-step. ActivityKit replaces the content-state wholesale on each
// push (it does not merge), so the server emits the full shape derived from the
// task row — not a partial patch. Checklist-step *perfect* parity with the
// operator's in-flight local taps is out of scope: a push reflects the DB's view
// of the checklist, which trails an un-synced optimistic tap by one write.
const LiveActivityContentStateSchema = z.object({
  currentStep: z.string().nullable().optional(),
  currentStepId: z.string().nullable().optional(),
  etaText: z.string().nullable().optional(),
  nextStep: z.string().nullable().optional(),
  nextStepId: z.string().nullable().optional(),
  status: z.enum(taskStatusValues),
  stepsDone: z.number().int().nonnegative(),
  stepsTotal: z.number().int().nonnegative(),
  topCue: z.string().nullable().optional(),
});
export type LiveActivityContentState = z.infer<
  typeof LiveActivityContentStateSchema
>;
